import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ChevronDown, Check, X } from 'lucide-react';

// Helper to parse date string
const parseDateString = (dateStr) => {
  if (!dateStr) return { day: '', month: '', year: '' };
  const [day, month, year] = dateStr.split('-');
  if (day && month && year) {
    return { day: parseInt(day), month: parseInt(month), year: parseInt(year) };
  }
  return { day: '', month: '', year: '' };
};

// Helper to parse time string
const parseTimeString = (timeStr) => {
  if (!timeStr) return { hour: '', minute: '', period: '' };
  const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s?(AM|PM)?/i);
  if (timeMatch) {
    return {
      hour: parseInt(timeMatch[1]),
      minute: parseInt(timeMatch[2]),
      period: timeMatch[3]?.toUpperCase() || 'AM'
    };
  }
  return { hour: '', minute: '', period: '' };
};

const ImprovedDateTimePicker = ({ onDateChange, onTimeChange, initialDate, initialTime, datePlaceholder }) => {
  // Parse initial values once using lazy initializer
  const [initialDateParsed] = useState(() => parseDateString(initialDate));
  const [initialTimeParsed] = useState(() => parseTimeString(initialTime));
  
  // Date states - initialize from parsed values
  const [selectedDay, setSelectedDay] = useState(initialDateParsed.day);
  const [selectedMonth, setSelectedMonth] = useState(initialDateParsed.month);
  const [selectedYear, setSelectedYear] = useState(initialDateParsed.year);
  
  // Time states - committed values
  const [selectedHour, setSelectedHour] = useState(initialTimeParsed.hour);
  const [selectedMinute, setSelectedMinute] = useState(initialTimeParsed.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(initialTimeParsed.period);
  
  // Time pending states (JP-136 F-2) - keeps selection isolated until Confirm
  const [pendingHour, setPendingHour] = useState(initialTimeParsed.hour);
  const [pendingMinute, setPendingMinute] = useState(initialTimeParsed.minute);
  const [pendingPeriod, setPendingPeriod] = useState(initialTimeParsed.period);

  // Year search typed input (JP-136 F-1)
  const [yearQuery, setYearQuery] = useState('');
  
  // UI states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');

  // Month names for display
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate arrays for dropdowns
  const days = Array.from({length: 31}, (_, i) => i + 1);
  const maxYear = 2100;
  const minYear = 1920;
  const years = Array.from({length: maxYear - minYear + 1}, (_, i) => maxYear - i);
  const hours = Array.from({length: 12}, (_, i) => i + 1); // 1, 2, 3, ..., 11, 12
  const minutes = Array.from({length: 60}, (_, i) => i);

  // Validate and update date
  const handleDateSelection = (day, month, year) => {
    setDateError('');
    
    if (!day || !month || !year) {
      setDateError('Please select day, month and year');
      return;
    }

    // Validate date
    const date = new Date(year, month - 1, day);
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
      setDateError('Invalid date selected');
      return;
    }

    // Format and send date
    const formattedDate = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
    onDateChange(formattedDate);
    setShowDatePicker(false);
  };

  // Validate and update time
  const handleTimeSelection = (hour, minute, period) => {
    setTimeError('');
    
    if (!hour || minute === '' || !period) {
      setTimeError('Please select hour, minute and AM/PM');
      return;
    }

    // Commit pending to selected
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setSelectedPeriod(period);

    // Format and send time
    const formattedTime = `${hour}:${minute.toString().padStart(2, '0')} ${period}`;
    onTimeChange(formattedTime);
    setShowTimePicker(false);
  };

  // References for scrolling selector lists
  const yearContainerRef = useRef(null);

  // Auto-scroll year container to selected year on picker open or input change
  const scrollToYear = useCallback((yearVal) => {
    if (!yearVal || !yearContainerRef.current) return;
    const container = yearContainerRef.current;
    const targetBtn = container.querySelector(`[data-year="${yearVal}"]`);
    if (targetBtn) {
      targetBtn.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (showDatePicker && selectedYear) {
      setTimeout(() => scrollToYear(selectedYear), 100);
    }
  }, [showDatePicker, selectedYear, scrollToYear]);

  const handleYearQueryChange = (e) => {
    const val = e.target.value;
    setYearQuery(val);
    const parsedYear = parseInt(val, 10);
    if (parsedYear >= 1920 && parsedYear <= 2100) {
      setSelectedYear(parsedYear);
      scrollToYear(parsedYear);
    }
  };

  // Get formatted display values
  const getDateDisplay = () => {
    if (selectedDay && selectedMonth && selectedYear) {
      return `${selectedDay} ${months[selectedMonth - 1]} ${selectedYear}`;
    }
    return datePlaceholder || 'Select your birth date';
  };

  const getTimeDisplay = () => {
    if (selectedHour && selectedMinute !== '' && selectedPeriod) {
      return `${selectedHour}:${selectedMinute.toString().padStart(2, '0')} ${selectedPeriod}`;
    }
    return 'Select birth time';
  };

  return (
    <div className="improved-datetime-picker space-y-6">
      {/* Date Picker */}
      <div className="datetime-section">
        <label className="datetime-label">Date of Birth</label>
        
        <motion.div
          className={`datetime-display-box ${showDatePicker ? 'active' : ''} ${dateError ? 'error' : ''}`}
          onClick={() => setShowDatePicker(!showDatePicker)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Calendar className="w-5 h-5 text-purple-400" />
          <span className={selectedDay && selectedMonth && selectedYear ? 'text-white' : 'text-gray-400'}>
            {getDateDisplay()}
          </span>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
        </motion.div>

        {dateError && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-400 text-sm mt-2 flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            {dateError}
          </motion.p>
        )}

        {typeof document !== 'undefined' && createPortal(
          <AnimatePresence>
            {showDatePicker && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="datetime-picker-backdrop"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 99999
                  }}
                  onClick={() => setShowDatePicker(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: "spring", duration: 0.3 }}
                  className="datetime-picker-panel"
                  style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 100000
                  }}
                >
                <h4 className="picker-title">Select Date of Birth</h4>
              
              <div className="date-grid">
                {/* Day Selector */}
                <div className="date-column">
                  <label className="column-label">Day</label>
                  <div 
                    className="selector-container"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {days.map(day => (
                      <motion.button
                        key={day}
                        type="button"
                        className={`date-option ${selectedDay === day ? 'selected' : ''}`}
                        onClick={() => setSelectedDay(day)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {day}
                        {selectedDay === day && <Check className="w-4 h-4 ml-2" />}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Month Selector */}
                <div className="date-column">
                  <label className="column-label">Month</label>
                  <div 
                    className="selector-container"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {months.map((month, index) => (
                      <motion.button
                        key={index}
                        type="button"
                        className={`date-option ${selectedMonth === index + 1 ? 'selected' : ''}`}
                        onClick={() => setSelectedMonth(index + 1)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {month}
                        {selectedMonth === index + 1 && <Check className="w-4 h-4 ml-2" />}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Year Selector */}
                <div className="date-column">
                  <label className="column-label">Year</label>
                  <div className="mb-2">
                    <input
                      type="number"
                      placeholder="Jump to year..."
                      value={yearQuery}
                      onChange={handleYearQueryChange}
                      min="1920"
                      max="2100"
                      className="w-full bg-white/10 border border-white/20 rounded-md text-white text-xs py-1.5 px-2 text-center focus:outline-none focus:border-purple-400"
                    />
                  </div>
                  <div 
                    ref={yearContainerRef}
                    className="selector-container"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {years.map(year => (
                      <motion.button
                        key={year}
                        type="button"
                        data-year={year}
                        className={`date-option ${selectedYear === year ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedYear(year);
                          setYearQuery(String(year));
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {year}
                        {selectedYear === year && <Check className="w-4 h-4 ml-2" />}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="picker-actions">
                <button
                  type="button"
                  onClick={() => setShowDatePicker(false)}
                  className="picker-btn secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDateSelection(selectedDay, selectedMonth, selectedYear)}
                  className="picker-btn primary"
                  disabled={!selectedDay || !selectedMonth || !selectedYear}
                >
                  Confirm Date
                </button>
              </div>
            </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
      </div>

      {/* Time Picker */}
      <div className="datetime-section">
        <label className="datetime-label">Time of Birth</label>
        
        <motion.div
          className={`datetime-display-box ${showTimePicker ? 'active' : ''} ${timeError ? 'error' : ''}`}
          onClick={() => setShowTimePicker(!showTimePicker)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Clock className="w-5 h-5 text-purple-400" />
          <span className={selectedHour && selectedMinute !== '' && selectedPeriod ? 'text-white' : 'text-gray-400'}>
            {getTimeDisplay()}
          </span>
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTimePicker ? 'rotate-180' : ''}`} />
        </motion.div>

        {timeError && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-400 text-sm mt-2 flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            {timeError}
          </motion.p>
        )}

        {typeof document !== 'undefined' && createPortal(
          <AnimatePresence>
            {showTimePicker && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="datetime-picker-backdrop"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 99999
                  }}
                  onClick={() => setShowTimePicker(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: "spring", duration: 0.3 }}
                  className="datetime-picker-panel"
                  style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 100000
                  }}
                >
                <h4 className="picker-title">Select Time of Birth</h4>
              
               <div className="time-grid">
                {/* Hour Selector */}
                <div className="time-column">
                  <label className="column-label">Hour</label>
                  <div 
                    className="selector-container small"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {hours.map(hour => (
                      <motion.button
                        key={hour}
                        type="button"
                        className={`time-option ${pendingHour === hour ? 'selected' : ''}`}
                        onClick={() => setPendingHour(hour)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {hour}
                        {pendingHour === hour && <Check className="w-3 h-3 ml-1" />}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Minute Selector */}
                <div className="time-column">
                  <label className="column-label">Minute</label>
                  <div 
                    className="selector-container small"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {minutes.map(minute => (
                      <motion.button
                        key={minute}
                        type="button"
                        className={`time-option ${pendingMinute === minute ? 'selected' : ''}`}
                        onClick={() => setPendingMinute(minute)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {minute.toString().padStart(2, '0')}
                        {pendingMinute === minute && <Check className="w-3 h-3 ml-1" />}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* AM/PM Selector */}
                <div className="time-column">
                  <label className="column-label">Period</label>
                  <div 
                    className="selector-container"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {['AM', 'PM'].map(period => (
                      <motion.button
                        key={period}
                        type="button"
                        className={`time-option large ${pendingPeriod === period ? 'selected' : ''}`}
                        onClick={() => setPendingPeriod(period)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {period}
                        {pendingPeriod === period && <Check className="w-4 h-4 ml-2" />}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="picker-actions">
                <button
                  type="button"
                  onClick={() => {
                    // Reset pending to last committed value on cancel
                    setPendingHour(selectedHour);
                    setPendingMinute(selectedMinute);
                    setPendingPeriod(selectedPeriod);
                    setShowTimePicker(false);
                  }}
                  className="picker-btn secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleTimeSelection(pendingHour, pendingMinute, pendingPeriod)}
                  className="picker-btn primary"
                  disabled={!pendingHour || pendingMinute === '' || !pendingPeriod}
                >
                  Confirm Time
                </button>
              </div>
            </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
      </div>

      {/* Selected Values Display */}
      {(selectedDay && selectedMonth && selectedYear && selectedHour && selectedMinute !== '' && selectedPeriod) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="selected-values-display"
        >
          <div className="selected-header">
            <Check className="w-5 h-5 text-green-400" />
            <span className="text-green-400 font-semibold">Birth Details Confirmed</span>
          </div>
          <div className="selected-content">
            <p className="text-white">
              <span className="text-purple-400">Date:</span> {getDateDisplay()}
            </p>
            <p className="text-white">
              <span className="text-purple-400">Time:</span> {getTimeDisplay()}
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ImprovedDateTimePicker;